import { toInteger } from 'lodash';
import { getFramework } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import {
    createCanvas,
    createForeignObjectImageUrl,
    downScaleCanvas,
    exportSize,
    fetchVectorText,
    getDrawContainParams,
    loadImageAsync,
    serializerHtmlToString,
    serializeSvgText,
} from '@/utils/file-kit';
import { MediaType, type ILayerProps, type IMediaLayerProps } from '@/version-types';
import { loadVideoCover } from '../utils/video';

const applyLayerMask = async (layerCanvas: HTMLCanvasElement, layer: ILayerProps) => {
    const { width, height } = layer;
    const { maskURL } = layer.data as IMediaLayerProps;
    const resultWidth = toInteger(width);
    const resultHeight = toInteger(height);

    if (!maskURL) return layerCanvas;

    const { canvas: maskCanvas, ctx: maskCtx } = createCanvas(resultWidth, resultHeight);
    let finalUrl = maskURL;

    if (/\.svg$/.test(maskURL)) {
        const svgMaskText = await fetchVectorText(maskURL);
        const { width, height } = exportSize(svgMaskText);
        finalUrl = createForeignObjectImageUrl(
            Math.floor(width),
            Math.floor(height),
            serializerHtmlToString(serializeSvgText(svgMaskText)),
            true,
        );
    }

    const maskImg = await loadImageAsync(finalUrl);
    const res = getDrawContainParams(maskImg.width, maskImg.height, resultWidth, resultHeight);
    maskCtx.drawImage(maskImg, res.dx, res.dy, res.dw, res.dh);
    maskCtx.globalCompositeOperation = 'source-in';
    maskCtx.drawImage(layerCanvas, 0, 0);

    return maskCanvas;
};

const applyLayerOpacity = (layerCanvas: HTMLCanvasElement, opacity: number) => {
    const { canvas: destCanvas, ctx: destCtx } = createCanvas(layerCanvas.width, layerCanvas.height);
    destCtx.globalAlpha = opacity;
    destCtx.drawImage(layerCanvas, 0, 0, layerCanvas.width, layerCanvas.height);
    return destCanvas;
};

const loadBaseImage = async (layer: ILayerProps, identity: string, needScaleCanvas: boolean) => {
    const { width, height } = layer;
    const { url, mediaType, poster, naturalWidth: domWidth, imageTransform } = layer.data as IMediaLayerProps;
    const { widthRatio, heightRatio, scale } = imageTransform;

    await base64Kit.getRemoteUrlAsync(url);

    let baseImage: HTMLCanvasElement | HTMLImageElement;
    try {
        const image =
            mediaType === MediaType.Video ? await loadVideoCover(poster, url) : await loadImageAsync(url, true);
        baseImage = createCanvas(image.width, image.height, image).canvas;
    } catch (_e) {
        const framework = getFramework(identity);
        framework.service.reportImageError({ layerId: layer.id, imageUrl: url });
        return createCanvas(1, 1).canvas;
    }

    const scaleCanvas = parseFloat(Math.floor(width / domWidth).toFixed(2));
    if (needScaleCanvas && scaleCanvas < 0.5 && widthRatio === 1 && heightRatio === 1 && scale === 1) {
        console.time(`图片压缩处理${layer.id}`);
        baseImage = downScaleCanvas(baseImage as HTMLCanvasElement, width, height);
        console.timeEnd(`图片压缩处理${layer.id}`);
    }

    return baseImage;
};

const applyLayerTransform = (layer: ILayerProps, baseImage: HTMLCanvasElement | HTMLImageElement) => {
    const { width, height } = layer;
    const {
        imageTransform: {
            flipH,
            flipV,
            scale,
            rotate: imgRotate,
            widthRatio,
            heightRatio,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
        },
    } = layer.data as IMediaLayerProps;

    const resultWidth = toInteger(width);
    const resultHeight = toInteger(height);
    const offsetX = toInteger(offsetXRatio * widthRatio * width * scale);
    const offsetY = toInteger(offsetYRatio * heightRatio * height * scale);
    const originImgWidth = toInteger(width * widthRatio);
    const originImgHeight = toInteger(height * heightRatio);
    let imgOffsetX = resultWidth / 2;
    let imgOffsetY = resultHeight / 2;

    if (flipH) {
        imgOffsetX = imgOffsetX - offsetX;
    } else {
        imgOffsetX = imgOffsetX + offsetX;
    }

    if (flipV) {
        imgOffsetY = imgOffsetY - offsetY;
    } else {
        imgOffsetY = imgOffsetY + offsetY;
    }

    let rotate = imgRotate;
    if (flipH) {
        rotate = -rotate;
    }
    if (flipV) {
        rotate = -rotate;
    }

    const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);
    destCtx.save();
    destCtx.translate(imgOffsetX, imgOffsetY);
    destCtx.rotate((rotate * Math.PI) / 180);
    destCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
    destCtx.drawImage(baseImage, -originImgWidth / 2, -originImgHeight / 2, originImgWidth, originImgHeight);
    destCtx.restore();

    return destCanvas;
};

export const renderLayerContentToCanvas = async (
    layer: ILayerProps,
    identity: string,
    globalAlpha = true,
    needScaleCanvas = true,
): Promise<HTMLCanvasElement> => {
    const baseImage = await loadBaseImage(layer, identity, needScaleCanvas);
    const layerCanvas = applyLayerTransform(layer, baseImage);
    const maskedCanvas = await applyLayerMask(layerCanvas, layer);

    if (!globalAlpha) return maskedCanvas;
    return applyLayerOpacity(maskedCanvas, layer.opacity);
};
